import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../ui/Button';
import retailerSettlement from '../../../services/retailerSettlement';
import xeroConnectionService from '../../../services/xeroConnection';
import { errorMessage } from '../../../utils/apiResponse';
import type { RemittanceXeroLists, RemittanceXeroSettings as Settings } from '../../../types/retailerSettlement';

/**
 * The Bunnings remittances section of Accounts → Xero (phase 3 of
 * docs/bunnings-remittance-plan.md).
 *
 * Switched on, each claim recorded on /accounts/remittances goes to Xero as a
 * credit note (Volume Rebate) or a bill (Marketing and Consolidation), at
 * Bunnings' own ex-GST and GST figures, and each recorded remittance is posted
 * through a clearing bank account so that one transfer is the deposit.
 *
 * It needs two permissions the ordinary sign-in does not ask for (payments and
 * bank transfers), so the first time it is set up Xero is reconnected with
 * them. Any accounts seat reads this; an Accounts Admin changes it.
 */

interface Props {
  connected: boolean;
  /** The connection reaches the books. */
  ready: boolean;
  isAdmin: boolean;
}

const INPUT = 'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm';
const LABEL = 'block text-sm font-medium text-neutral-700 mb-1';

export default function RemittanceXeroSettings({ connected, ready, isAdmin }: Props) {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [lists, setLists] = useState<RemittanceXeroLists | null>(null);
  const [form, setForm] = useState({ enabled: false, clearing: '', bank: '', services: '', tax: 'INPUT' });
  const [busy, setBusy] = useState<'save' | 'connect' | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const show = useCallback((s: Settings) => {
    setSettings(s);
    setForm({
      enabled: s.enabled,
      clearing: s.clearing_account_code ?? '',
      bank: s.bank_account_code ?? '',
      services: s.services_account_code ?? '',
      tax: s.input_tax_type || 'INPUT',
    });
  }, []);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await retailerSettlement.getXeroSettings();
        if (live && res.success && res.data) show(res.data);
      } catch (e) {
        if (live) setError(errorMessage(e, 'Could not load the remittance settings'));
      }
      if (!isAdmin || !ready) return;
      try {
        const res = await retailerSettlement.getXeroLists();
        if (live && res.success && res.data) setLists(res.data);
      } catch (e) {
        if (live) setError(errorMessage(e, 'Could not read the accounts from Xero'));
      }
    })();
    return () => { live = false; };
  }, [isAdmin, ready, show]);

  const save = async () => {
    setBusy('save');
    setError(null);
    setNotice(null);
    try {
      const res = await retailerSettlement.saveXeroSettings({
        enabled: form.enabled,
        clearing_account_code: form.clearing || null,
        bank_account_code: form.bank || null,
        services_account_code: form.services || null,
        input_tax_type: form.tax || null,
      });
      if (res.success && res.data) {
        show(res.data);
        setNotice(res.data.enabled
          ? (res.data.can_post
            ? 'Saved. Claims and remittances recorded from now on go to Xero.'
            : 'Saved. Reconnect Xero below to give it the two permissions it needs; nothing goes until then.')
          : 'Saved. Claims and remittances stay here only.');
      } else setError(errorMessage(res, 'Could not save'));
    } catch (e) {
      setError(errorMessage(e, 'Could not save'));
    } finally {
      setBusy(null);
    }
  };

  const reconnect = async () => {
    setBusy('connect');
    try {
      const res = await xeroConnectionService.getAuthorizeUrl(true);
      if (res.success && res.data?.authorization_url) window.location.href = res.data.authorization_url;
      else setError(errorMessage(res, 'Could not start the Xero sign-in'));
    } catch (e) {
      setError(errorMessage(e, 'Could not start the Xero sign-in'));
    } finally {
      setBusy(null);
    }
  };

  const account = (code: string | null, list: { code: string; name: string }[] | undefined) => {
    if (!code) return 'Not chosen';
    const a = list?.find((x) => x.code === code);
    return a ? `${a.code} — ${a.name}` : code;
  };

  const select = (id: string, label: string, value: string, onChange: (v: string) => void,
    options: { code: string; name: string }[], help: string) => (
    <div className="min-w-0">
      <label htmlFor={id} className={LABEL}>{label}</label>
      <select id={id} value={value} className={INPUT} onChange={(e) => onChange(e.target.value)}>
        <option value="">Choose…</option>
        {value && !options.some((o) => o.code === value) && <option value={value}>{value}</option>}
        {options.map((o) => <option key={o.code} value={o.code}>{o.code} — {o.name}</option>)}
      </select>
      <p className="mt-1 text-xs text-neutral-500 break-words">{help}</p>
    </div>
  );

  return (
    <section className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3">
      <h2 className="text-base font-semibold text-neutral-900 break-words">Post Bunnings remittances</h2>
      <p className="text-sm text-neutral-700 break-words">
        Each claim recorded on <Link className="text-primary-700" to="/accounts/remittances">Remittances</Link> goes
        to Xero at Bunnings&rsquo; own figures, and each recorded remittance is posted through a clearing account so
        that one transfer is the deposit: match that transfer to the bank line in Xero and press OK.
      </p>
      <ul className="text-sm text-neutral-700 list-disc pl-5 space-y-1 break-words">
        <li>
          <strong>Volume Rebate</strong> (Bunnings sends a payment request): a credit note on the head office, to the
          rebate account, at the GST rate on sales. It takes the GST off your sales for the month of the claim.
        </li>
        <li>
          <strong>Marketing and Consolidation</strong> (Bunnings sends a tax invoice): a bill under Bunnings&rsquo; claim
          number, to the account below, at the purchases GST rate. You claim that GST back for the month of the claim.
        </li>
        <li>
          The ex-GST amount and the GST on each are Bunnings&rsquo; to the cent, so your BAS shows what their documents
          show. Payments and the transfer carry no GST. On cash-basis GST, Xero counts each one&rsquo;s GST as it is
          paid down, remittance by remittance.
        </li>
        <li>Only what is recorded after this is switched on goes. Anything earlier was reconciled by hand.</li>
      </ul>

      {!connected ? (
        <p className="text-sm text-neutral-500 break-words">Connect Xero above to post remittances to it.</p>
      ) : !ready ? (
        <p className="text-sm text-neutral-500 break-words">Reconnect Xero above so it can reach the books first.</p>
      ) : settings && (
        <>
          {notice && <p className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2 break-words" role="status">{notice}</p>}
          {error && <p role="alert" className="text-sm text-red-700 break-words">{error}</p>}

          {settings.enabled && !settings.can_post && (
            <div className="space-y-2">
              <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 break-words">
                Xero has to allow this connection to record payments and bank transfers. Reconnect: your organisation shows
                already selected, so press Continue, and approve the new permissions. Nothing already set up is lost.
              </p>
              {isAdmin && (
                <Button size="sm" onClick={reconnect} disabled={busy === 'connect'}>
                  {busy === 'connect' ? 'Opening Xero…' : 'Reconnect Xero for remittances'}
                </Button>
              )}
            </div>
          )}

          {settings.problems.length > 0 && settings.enabled && (
            <p className="text-sm text-amber-900 break-words">Still needed: {settings.problems.join(', ')}.</p>
          )}

          {isAdmin ? (
            <>
              <label className="flex items-start gap-2 text-sm text-neutral-800">
                <input type="checkbox" className="mt-0.5 h-4 w-4" checked={form.enabled}
                  onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))} />
                <span className="min-w-0 break-words">Post Bunnings claims and remittances to Xero.</span>
              </label>
              {select('remit-clearing', 'Clearing account', form.clearing, (v) => setForm((f) => ({ ...f, clearing: v })),
                lists?.bank_accounts ?? [],
                'A bank account in Xero with no bank feed, such as "Bunnings clearing". Every remittance passes through it and leaves it at $0.')}
              {select('remit-bank', 'Bank account the deposit lands in', form.bank, (v) => setForm((f) => ({ ...f, bank: v })),
                lists?.bank_accounts ?? [],
                'The transfer out of clearing goes here, and is what you match to the deposit on the bank feed.')}
              {select('remit-services', "Account for Bunnings' services", form.services, (v) => setForm((f) => ({ ...f, services: v })),
                lists?.expense_accounts ?? [],
                'Where the Marketing Allowance and Greenlife Consolidation bills are expensed.')}
              <div className="min-w-0">
                <label htmlFor="remit-tax" className={LABEL}>GST rate on those bills</label>
                <select id="remit-tax" value={form.tax} className={INPUT} onChange={(e) => setForm((f) => ({ ...f, tax: e.target.value }))}>
                  {!(lists?.tax_rates ?? []).some((t) => t.tax_type === form.tax) && (
                    <option value={form.tax}>{form.tax === 'INPUT' ? 'GST on Expenses (INPUT)' : form.tax}</option>
                  )}
                  {(lists?.tax_rates ?? []).map((t) => <option key={t.tax_type} value={t.tax_type}>{t.name} ({Number(t.rate)}%)</option>)}
                </select>
                <p className="mt-1 text-xs text-neutral-500 break-words">
                  The credit note uses the rebate account and the GST rate on sales from the Invoices section.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button onClick={save} disabled={busy === 'save'}>{busy === 'save' ? 'Saving…' : 'Save'}</Button>
              </div>
            </>
          ) : (
            <>
              <dl className="space-y-2">
                {[
                  ['Posted to Xero', settings.enabled ? 'Yes' : 'No'],
                  ['Clearing account', account(settings.clearing_account_code, lists?.bank_accounts)],
                  ['Deposit account', account(settings.bank_account_code, lists?.bank_accounts)],
                  ["Bunnings' services", account(settings.services_account_code, lists?.expense_accounts)],
                ].map(([k, v]) => (
                  <div key={k} className="min-w-0">
                    <dt className="text-xs text-neutral-500">{k}</dt>
                    <dd className="text-sm text-neutral-800 break-words">{v}</dd>
                  </div>
                ))}
              </dl>
              <p className="text-sm text-neutral-500 break-words">An Accounts Admin can change this.</p>
            </>
          )}

          {settings.enabled && (
            <p className="text-sm text-neutral-600 break-words">
              Waiting: {settings.queue.claims_pending} claim{settings.queue.claims_pending === 1 ? '' : 's'},{' '}
              {settings.queue.remittances_pending} remittance{settings.queue.remittances_pending === 1 ? '' : 's'}.
              {(settings.queue.claims_failed + settings.queue.remittances_failed) > 0 && (
                <> Failed: {settings.queue.claims_failed + settings.queue.remittances_failed}, with the reason on <Link className="text-primary-700" to="/accounts/remittances">Remittances</Link>.</>
              )}
            </p>
          )}
        </>
      )}
    </section>
  );
}
